import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import {useMemo, useState} from "react";
import {useNavigate} from "react-router-dom-v5-compat";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingSmall,
    LabelMedium,
} from "@khanacademy/wonder-blocks-typography";
import {
    EmailTextField,
    getErrorMessageFromUnknownError,
    isEmailValid,
    PASSWORD_MIN_LENGTH,
    PasswordTextField,
    signupUnderAgeGate,
    UsernameTextField,
} from "@khan/auth-password";
import {getContinueUrl} from "@khan/auth-shared";
import {KAError} from "@khan/logging";
import {parseUrl, redirect, useLocation} from "@khan/urls";

import {useApolloGqlClient} from "~/data-access/gql/use-apollo-gql-client.ts";

import {BackLink} from "./back-link.tsx";
import {StandardSignupLegalBlurb} from "./legal-blurb.tsx";
import sharedStyles from "./shared-styles.ts";

interface Props {
    // Text which displays below the heading. Provides extra info to the user
    subHeading?: string;
    // The path to navigate to after the user has signed up
    nextPath: string;
    // The path for the back link
    backlinkPath: string;
    // The text for the back link
    backlinkText?: string;
    // Hide the back link or not
    hideBackLink?: boolean;
    // Maximum width of the component
    maxWidth?: string;
    // Callback triggered after a user has signed up, but before they are
    // redirected to the next page.
    onSubmitSuccess?: () => Promise<void>;
    // Whether to hide the parent email field
    hideParentField?: boolean;
    // If there isn't a continue= query param, use this path as the default
    defaultContinuePath?: string;
    // The class code to use for sign up.
    classCode?: string;
}

/**
 * A form for the U13 user to enter their username, parent email, and password
 * during sign up.
 *
 * Does not include the option to sign up with a 3rd parties such as Google.
 */
export const SignupUnderageForm = ({
    subHeading,
    nextPath,
    backlinkPath,
    backlinkText = t`Back`,
    maxWidth = "100%",
    hideBackLink = false,
    onSubmitSuccess,
    hideParentField = false,
    defaultContinuePath = "/",
    classCode = "",
}: Props) => {
    const [loading, setLoading] = useState(false);
    const [username, setUsername] = useState("");
    const [parentEmail, setParentEmail] = useState("");
    const [password, setPassword] = useState("");
    const [signupError, setSignupError] = useState<string | React.ReactElement>(
        "",
    );
    const navigate = useNavigate();
    const location = useLocation();
    const {gqlClient} = useApolloGqlClient();

    const birthdate = useMemo(() => {
        return sessionStorage.getItem("new_signup_birthday") ?? "";
    }, []);

    if (!birthdate) {
        navigate(backlinkPath);
        return;
    }

    const onUsernameChange = (value: string) => {
        setUsername(value);
    };

    const onParentEmailChange = (value: string) => {
        setParentEmail(value);
    };

    const onPasswordChange = (value: string) => {
        setPassword(value);
    };

    const finalRedirectUrl = getContinueUrl(location) ?? defaultContinuePath;
    const continueUrl = parseUrl(nextPath);
    const backUrl = parseUrl(backlinkPath);
    if (finalRedirectUrl && finalRedirectUrl !== defaultContinuePath) {
        continueUrl.searchParams.set("continue", finalRedirectUrl);
        backUrl.searchParams.set("continue", finalRedirectUrl);
    }

    const onSignup = async () => {
        // Check if valid
        if (
            (!hideParentField && !isEmailValid(parentEmail)) ||
            password.length < PASSWORD_MIN_LENGTH
        ) {
            setSignupError(t`Please fill out all required fields.`);
            return;
        }

        try {
            setLoading(true);
            await signupUnderAgeGate(gqlClient, {
                parentEmail,
                password,
                username,
                birthdate,
                signupCodes: classCode ? [classCode] : undefined,
            });

            sessionStorage.removeItem("new_signup_birthday");

            if (onSubmitSuccess) {
                await onSubmitSuccess();
            }

            redirect(continueUrl.pathname + continueUrl.search);
        } catch (error) {
            if (error instanceof Error || error instanceof KAError) {
                const message = getErrorMessageFromUnknownError(
                    error,
                    t`Oops! Something went wrong. Please try again.`,
                );
                setSignupError(message);
            } else {
                setSignupError("An error occurred, please try again later.");
            }
        } finally {
            setLoading(false);
        }
    };

    const passwordTooltip = t`Passwords should be at least ${PASSWORD_MIN_LENGTH} characters long and should contain a mixture of letters, numbers, and other characters.`;

    const usernameTooltip = t`Use letters and numbers only. For safety, don't use your real name.`;

    return (
        <form
            action=""
            onSubmit={async (evt: React.SyntheticEvent): Promise<void> => {
                evt.preventDefault();
                await onSignup();
            }}
        >
            <View style={{maxWidth}}>
                {!hideBackLink && (
                    <>
                        <BackLink
                            text={backlinkText}
                            backUrl={backUrl.pathname + backUrl.search}
                            style={styles.backlink}
                        />
                        <Strut size={spacing.xSmall_8} />
                    </>
                )}
                <View style={styles.fieldsHeader}>
                    <HeadingSmall>{t`Sign up as a learner today!`}</HeadingSmall>
                    <LabelMedium>
                        <span className={css(styles.requiredAsterisk)}>*</span>
                        <span className={css(styles.requiredText)}>
                            {" "}
                            {t`required field`}
                        </span>
                    </LabelMedium>
                </View>
                {subHeading && (
                    <>
                        <Strut size={spacing.xSmall_8} />
                        <LabelMedium style={styles.subHeading}>
                            {subHeading}
                        </LabelMedium>
                    </>
                )}
                {!hideParentField && (
                    <>
                        <Strut size={spacing.large_24} />
                        <EmailTextField
                            required={true}
                            onChange={onParentEmailChange}
                            label={t`What is your parent or guardian's email?`}
                            description={t`You'll need permission to use Khan Academy`}
                            initialValue={parentEmail}
                        />
                    </>
                )}
                <Strut size={spacing.large_24} />
                <UsernameTextField
                    label={t`Choose a username`}
                    onChange={onUsernameChange}
                    value={username}
                    required={true}
                    tooltip={usernameTooltip}
                />
                <Strut size={spacing.large_24} />
                <PasswordTextField
                    required={true}
                    label={t`Password`}
                    onChange={onPasswordChange}
                    initialValue={password}
                    tooltip={passwordTooltip}
                />
                <Strut size={spacing.large_24} />
                <Button
                    type="submit"
                    style={styles.signupButton}
                    spinner={loading}
                >
                    {t`Sign up`}
                </Button>
                {signupError && (
                    <Body
                        tag="p"
                        role="alert"
                        style={sharedStyles.fieldError}
                        aria-live="polite"
                    >
                        {signupError}
                    </Body>
                )}
                <Strut size={spacing.large_24} />
                <StandardSignupLegalBlurb />
            </View>
        </form>
    );
};

const styles = StyleSheet.create({
    backlink: {
        alignSelf: "flex-start",
    },
    fieldsHeader: {
        width: "100%",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
    },
    requiredAsterisk: {
        color: color.red,
    },
    requiredText: {
        color: color.offBlack64,
    },
    signupButton: {
        width: "100%",
    },
    subHeading: {color: color.offBlack64},
});
